/**
 * Тонкий fetch-обёртчик над FastAPI-эндпоинтами. Без axios — fetch достаточно
 * для нашего набора: 3-4 endpoints, без upload/streaming.
 *
 * В dev-моде запросы идут через Vite proxy (vite.config.ts → /api → FastAPI).
 * В prod — на тот же origin что и фронтенд (FastAPI отдаёт static React-build).
 */

import type {
  CreateRunResponse,
  PrinciplesCatalog,
  RunRequest,
  RunResponse,
  RunSummary,
} from "./types";

const BASE = "/api";

async function jsonFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const resp = await fetch(`${BASE}${url}`, {
    ...init,
    credentials: "include", // отправлять/принимать httpOnly cookie metatriz_auth
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers ?? {}),
    },
  });
  if (!resp.ok) {
    const text = await resp.text();
    const err = new Error(`${resp.status} ${resp.statusText}: ${text}`) as Error & {
      status?: number;
    };
    err.status = resp.status;
    throw err;
  }
  return resp.json() as Promise<T>;
}

export const api = {
  health(): Promise<{ status: string; version: string }> {
    return jsonFetch("/health");
  },

  // --- Auth ---
  login(username: string, password: string): Promise<{ username: string }> {
    return jsonFetch("/auth/login", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    });
  },
  me(): Promise<{ username: string }> {
    return jsonFetch("/auth/me");
  },
  logout(): Promise<{ ok: boolean }> {
    return jsonFetch("/auth/logout", { method: "POST" });
  },

  createRun(req: RunRequest): Promise<CreateRunResponse> {
    return jsonFetch("/runs", {
      method: "POST",
      body: JSON.stringify(req),
    });
  },

  listRuns(limit = 50): Promise<RunSummary[]> {
    return jsonFetch(`/runs?limit=${limit}`);
  },

  getRun(id: string): Promise<RunResponse> {
    return jsonFetch(`/runs/${encodeURIComponent(id)}`);
  },

  deleteRun(id: string): Promise<{ run_id: string; deleted: boolean }> {
    return jsonFetch(`/runs/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
  getTree(rootOrAnyRunId: string): Promise<RunSummary[]> {
    return jsonFetch(`/runs/${encodeURIComponent(rootOrAnyRunId)}/tree`);
  },

  // --- KB ---
  principles(): Promise<PrinciplesCatalog> {
    return jsonFetch("/principles");
  },
};
